import { NavLink, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { AuthStatus, useAuthContext } from "../hooks/useAuthContext";
import { Ripple } from "primereact/ripple";
import MeaTopNavbar from "./components/MeaTopNavbar";
import { useEffect, useMemo, useRef } from "react";
import { Menu } from "primereact/menu";
import { getAvatarMenuItems } from "./components/MeaAvatarDropdown";
import { useMemberActivityTracking } from "../hooks/useMemberActivityTracking";
import accountsAPI from "../services/accounts";

export function SchoolLayout() {
  const { account, status, lastSchoolId, switchSchoolMembership, currentMembership, updateAccountPreferences, logout } = useAuthContext();
  const profileMenu = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { trackActivity } = useMemberActivityTracking();

  const availableMemberships = useMemo(() => {
    return (account?.memberships || []).filter((membership) => {
      if (!membership?.school_id) {
        return false;
      }
      if (!membership?.school) {
        return true;
      }
      return membership?.school?.status === "ACTIVE";
    });
  }, [account?.memberships]);

  useEffect(() => {
    if (status === AuthStatus.GUEST) {
      navigate("/auth/login", { state: { from: location } });
    }
  }, [status, location, navigate]);

  useEffect(() => {
    if (status !== AuthStatus.AUTHENTICATED) {
      return;
    }
    if (location.pathname === '/school' || location.pathname === '/school/') {
      if (availableMemberships.length === 0) {
        navigate('/user/schools');
      } else {
        const preferredSchoolId = account?.preferences?.last_visited_school || lastSchoolId;
        const preferredMembership = availableMemberships.find((membership) => membership.school_id === preferredSchoolId);
        if (preferredMembership?.school_id) {
          navigate('/school/' + preferredMembership.school_id, { replace: true });
        } else {
          navigate('/user/schools', { replace: true });
        }
      }
    }

  }, [status, location.pathname, account, lastSchoolId, availableMemberships, navigate]);

  useEffect(() => {
    if (status !== AuthStatus.AUTHENTICATED) {
      return;
    }
    if (params.schoolId) {
      const targetMembership = availableMemberships.find((membership) => membership.school_id === params.schoolId);
      if (!targetMembership) {
        navigate('/user/schools', { replace: true });
        return;
      }
      if (currentMembership?.id !== targetMembership.id) {
        switchSchoolMembership(targetMembership);
      }
      
      const currentPref = account?.preferences?.last_visited_school;
      if (currentPref !== targetMembership.school_id) {
        accountsAPI.UpdateCurrentAccountPreferences("", targetMembership.school_id)
          .then((updatedPrefs) => {
            if (updatedPrefs) {
              updateAccountPreferences(updatedPrefs);
            } else {
              const fallbackPrefs = account?.preferences
                ? { ...account.preferences, last_visited_school: targetMembership.school_id }
                : { last_visited_school: targetMembership.school_id };
              updateAccountPreferences(fallbackPrefs);
            }
          })
          .catch((err) => {
            console.error("Failed to update last visited school preference", err);
          });
      }
    }
  }, [status, params.schoolId, account, switchSchoolMembership, updateAccountPreferences, availableMemberships, currentMembership?.id, navigate]);

  // Track member activity when school layout is rendered
  useEffect(() => {
    if (currentMembership && status === AuthStatus.AUTHENTICATED) {
      trackActivity(""); // Empty access token since we're using credentials: "include"
    }
  }, [currentMembership, status, trackActivity]);

  const mainNavItems = params.schoolId ? [
    (currentMembership?.is_owner || currentMembership?.is_admin) && { label: "Administration", icon: "pi pi-home", to: "/school/" + params.schoolId + "/admin" },
    currentMembership?.is_staff && { label: "Secretariat", icon: "pi pi-briefcase", to: "/school/" + params.schoolId + "/secretariat" },
    currentMembership?.is_teacher && { label: "Enseignant(e)s", icon: "pi pi-users", to: "/school/" + params.schoolId + "/teacher" },
  ].filter(Boolean) : [];
  const hasBottomNav = mainNavItems.length > 0;
  const profileMenuItems = [
    {
      template: () => (
        <div className="px-3 py-2 border-bottom-1 surface-border">
          <div className="text-900 font-medium">{account?.firstname} {account?.lastname}</div>
          <div className="text-600 text-sm">{account?.email}</div>
        </div>
      ),
    },
    ...getAvatarMenuItems({ account, logout, navigate }),
  ];

  return (
    <div style={{ height: "100vh" }}>
      <div className={"min-h-screen flex flex-column " + (hasBottomNav ? "mea-has-bottom-nav" : "")}>
        <div className="no-print">
          <MeaTopNavbar hideMobileMenu={hasBottomNav} mainMenu={
            <ul className="list-none p-0 m-0 select-none hidden lg:flex lg:flex-row gap-2">
              {mainNavItems.map((item) => (
                <li key={item.to} className="lg:flex lg:align-items-end">
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      'no-underline text-color p-ripple border-round-top flex align-items-center px-3 py-3 font-medium cursor-pointer transition-colors transition-duration-150 ' + (isActive ? 'bg-teal-100 text-900' : 'hover:surface-hover')
                    }
                  >
                    <i className={"pi mr-2 " + item.icon}></i>
                    <span>{item.label}</span>
                    <Ripple />
                  </NavLink>
                </li>
              ))}
            </ul>
          } />
        </div>
        <div className="flex flex-column flex-auto">
          <Outlet />
        </div>
        {hasBottomNav && (
          <nav className="mea-bottom-nav lg:hidden no-print" aria-label="Navigation principale">
            {mainNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => "mea-bottom-nav-item p-ripple" + (isActive ? " active" : "")}
              >
                <i className={"pi " + item.icon}></i>
                <span>{item.label}</span>
                <Ripple />
              </NavLink>
            ))}
            <button
              type="button"
              className="mea-bottom-nav-item mea-bottom-nav-profile p-ripple"
              aria-haspopup="menu"
              aria-controls="mea-bottom-profile-menu"
              onClick={(event) => profileMenu.current.toggle(event)}
            >
              <img src={account?.avatar || "/images/user.png"} alt="" className="mea-bottom-nav-avatar" />
              <span>Profil</span>
              <Ripple />
            </button>
            <Menu model={profileMenuItems} popup ref={profileMenu} id="mea-bottom-profile-menu" />
          </nav>
        )}
      </div>
    </div>
  )
}
